import Cookies from "js-cookie";
import {
  CONSENT_COOKIE_NAME,
  CONSENT_VERSION,
  parseConsent,
  type ConsentValue,
} from "./consent.shared";
import { DPID_COOKIE_NAME } from "./dpid";

export {
  CONSENT_COOKIE_NAME,
  CONSENT_VERSION,
  parseConsent,
  type ConsentValue,
};

const GOOGLE_ANALYTICS_COOKIE_NAMES = new Set(["_ga", "_gid", "_gat"]);
const SIX_MONTHS_IN_DAYS = 182;

function getCookiePath(): string {
  return (process.env.NEXT_PUBLIC_BASE_PATH || "").trim() || "/";
}

function isGoogleAnalyticsCookie(name: string): boolean {
  return (
    GOOGLE_ANALYTICS_COOKIE_NAMES.has(name) ||
    name.startsWith("_ga_") ||
    name.startsWith("_gat_")
  );
}

function getAnalyticsCookieNames(): string[] {
  return Object.keys(Cookies.get()).filter(isGoogleAnalyticsCookie);
}

function getCookieDomainCandidates(): Array<string | undefined> {
  // No domain attribute means "host-only"; that's the only valid option when
  // there is no real registrable browser hostname.
  if (typeof window === "undefined") return [undefined];

  const hostname = window.location.hostname;
  if (
    !hostname ||
    hostname === "localhost" ||
    hostname.includes(":") ||
    /^\d{1,3}(\.\d{1,3}){3}$/.test(hostname)
  ) {
    return [undefined];
  }

  const domains = new Set<string | undefined>([
    undefined,
    hostname,
    `.${hostname}`,
  ]);
  const parentDomain = hostname.split(".").slice(-2).join(".");
  domains.add(parentDomain);
  domains.add(`.${parentDomain}`);

  return Array.from(domains);
}

function getCookieRemovalOptions(): Cookies.CookieAttributes[] {
  const paths = Array.from(new Set([getCookiePath(), "/"]));
  const domains = getCookieDomainCandidates();

  return paths.flatMap((path) =>
    domains.map((domain) => (domain ? { path, domain } : { path })),
  );
}

export function getClientConsent(): ConsentValue | undefined {
  return parseConsent(Cookies.get(CONSENT_COOKIE_NAME));
}

export function setConsent(value: ConsentValue): void {
  Cookies.set(CONSENT_COOKIE_NAME, `${value}:${CONSENT_VERSION}`, {
    path: getCookiePath(),
    expires: SIX_MONTHS_IN_DAYS,
    sameSite: "Lax",
  });
}

export function deleteAnalyticsCookies(): void {
  const cookieNames = new Set([DPID_COOKIE_NAME, ...getAnalyticsCookieNames()]);
  const removalOptions = getCookieRemovalOptions();

  for (const name of cookieNames) {
    for (const options of removalOptions) {
      Cookies.remove(name, options);
    }
  }
}

export function deleteConsent(): void {
  Cookies.remove(CONSENT_COOKIE_NAME, { path: getCookiePath() });
  deleteAnalyticsCookies();
}
